/// <amd-module name="Environments/Landing/Components/EnvironmentsComponent" />
import React = require("react");

import { TaskResult } from "DistributedTask/Client/Contracts/TaskAgent";
import {
    EnvironmentBaseComponent,
    IEnvironmentBaseComponentProperties,
    IEnvironmentBaseComponentState
} from "Environments/EnvironmentsCommon/EnvironmentBaseComponent";
import { ActionCreatorManager } from "Environments/Flux/ActionCreatorManager";
import { StoreManager } from "Environments/Flux/StoreManager";
import { EnvironmentsActionsCreator } from "Environments/Landing/ActionsCreators/EnvironmentsActionsCreator";
import * as Resources from "Environments/Landing/Resources";
import { EnvironmentsStore } from "Environments/Landing/Stores/EnvironmentsStore";

import { getEnvironmentPageUrl, getEnvironmentsSecurityPageUrl } from "Environments/EnvironmentsCommon/Link";
import { UrlParamKeys } from "Environments/EnvironmentsCommon/Utils";
import * as Constants from "Environments/Landing/Common/Constants";

import { IReadonlyObservableArray, ObservableValue } from "VSS/Core/Observable";
import { getTabs } from "VSS/Features/PlatformUI/ContributedTab";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssComponentContext, VssComponent, WrappedComponent, css } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { IVssPerformanceService } from "VSS/Platform/Performance";

import { CheckIssue, CheckIssueType } from "Checks/Client/Contracts/PipelinesChecks";
import { EnvironmentJobStatus, IEnvironmentDeploymentJobRecord, IEnvironmentObject, IEnvironmentsResult } from "Environments/Client/EnvironmentContracts";
import { announce } from "VSS/Core/Util/Accessibility";
import { ScreenBreakpoints } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { Ago } from "VSSUI/Ago";
import { Card } from "VSSUI/Card";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { DropdownFilterBarItem } from "VSSUI/Dropdown";
import { FilterBar } from "VSSUI/FilterBar";
import { Header, IHeaderBreakpoint, TitleSize } from "VSSUI/Header";
import { HeaderCommandBarWithFilter, IHeaderCommandBarItem, getFilterItem } from "VSSUI/HeaderCommandBar";
import { Icon } from "VSSUI/Icon";
import { ListSelection } from "VSSUI/List";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Spinner, SpinnerSize } from 'VSSUI/Spinner';
import { IStatusProps, Status, StatusSize, Statuses } from "VSSUI/Status";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { ITableBreakpoint, ITableColumn, ITableRow, ITableRowDetails, SimpleTableCell, Table, TableCell, TableRow, TwoLineTableCell } from "VSSUI/Table";
import { IVssContributedTab, Tab, TabBar, TabContent } from "VSSUI/Tabs";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { Tooltip } from "VSSUI/TooltipEx";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { FILTER_CHANGE_EVENT, Filter, IFilterState } from "VSSUI/Utilities/Filter";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";


interface ILoadRow {
    isLoadRow?: boolean;
    renderRow?: (index: number, item: ITableItem, details: ITableRowDetails<ITableItem>) => JSX.Element
}

interface ITableItem extends Partial<IEnvironmentObject>, ILoadRow {
    issue?: CheckIssue
}

interface IState extends IEnvironmentBaseComponentState {
    itemProvider: ArrayItemProvider<ITableItem>;
    showZeroDataExperience: boolean;
}

const LoadingComponent = () => (
    <Spinner
        label={Resources.Loading}
        size={SpinnerSize.large}
        className="absolute-fill"
    />
);

export class EnvironmentsComponent extends EnvironmentBaseComponent<IEnvironmentBaseComponentProperties, IState> {

    public getInstanceId(context: IVssComponentContext): string {
        return "environments-instance-id-" + this.componentId;
    }

    public constructor(props: IEnvironmentBaseComponentProperties, context: IVssComponentContext) {
        super(props, context);

        const contributionService = context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const data = contributionService.getData<IEnvironmentsResult>(Constants.EnvironmentsDataProviderContribution)!;

        const itemProvider = this._getItemProvider(data.environments);

        this.state = {
            itemProvider: itemProvider,
            showZeroDataExperience: itemProvider.length === 0,
            errorMessages: {}
        };

        this._tabProvider = getTabs(context.pageContext, "ms.vss-environments-web.environments-tab-group");
        this._filter.subscribe(this._onFilterApplied, FILTER_CHANGE_EVENT);

        this._actionCreator = ActionCreatorManager.CreateActionCreator<EnvironmentsActionsCreator>(EnvironmentsActionsCreator, this._instanceId);
        this._store = StoreManager.CreateStore<EnvironmentsStore>(EnvironmentsStore, this._instanceId);
        this._store.addChangedListener(this._onStoreChanged);

        const historyService = context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const historyState = historyService.getState();
        if (historyState.createNew && parseInt(historyState[UrlParamKeys.CreateNew] as string) > 0) {
            this._addEnvironmentPanelOpened.value = true;
        }
    }

    public render(): JSX.Element {

        if (this.state.showZeroDataExperience && !this._filter.hasChangesToReset()) {
            return this._getZeroDataContent();
        }

        const tabCount = this._tabProvider.length;

        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow" selectedTabId={this._selectedTabId} tabProviders={this._tabProvider}>
                    <Header title={Resources.EnvironmentsTitleName} titleSize={TitleSize.Large} commandBarItems={this._getCommandBarItems(tabCount)} headerBreakpoints={this._getHeaderCommandBreakpoints()} />
                    {
                        tabCount > 0 &&
                        <>
                            <TabBar onSelectedTabChanged={this._onSelectedTabChanged} renderAdditionalContent={this._renderTabBarCommands}>
                                <Tab name={Resources.AvailableEnvironmentsTabText} id={Constants.EnvironmentsDefaultTabKey} />
                            </TabBar>
                            <TabContent>
                                {
                                    this._selectedTabId.value === Constants.EnvironmentsDefaultTabKey && this._availableTabContent()
                                }
                            </TabContent>
                        </>
                    }
                    {
                        tabCount === 0 &&
                        this._availableTabContent()
                    }
                </Page>
                {this._getAddEnvironmentDialogComponent()}
            </Surface>
        );
    }

    public componentDidMount(): void {
        this._markTTI();
        this._actionCreator.getEnvironmentWithFilters(this.context.pageContext, true);
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService.getContributionsAsync<any>(["ms.vss-environments-web.environments-create-panel"]);
    }

    public componentWillUnmount(): void {
        this._store.removeChangedListener(this._onStoreChanged);
        this._filter.unsubscribe(this._onFilterApplied, FILTER_CHANGE_EVENT);
        StoreManager.DeleteStore<EnvironmentsStore>(EnvironmentsStore, this._instanceId);
        ActionCreatorManager.DeleteActionCreator<EnvironmentsActionsCreator>(EnvironmentsActionsCreator, this._instanceId);
        super.componentWillUnmount();
    }

    private _onStoreChanged = (): void => {
        const environments = this._store.getEnvironments();
        const hasMore = !!this._store.getState().continuationToken;

        this.setState({
            itemProvider: this._getItemProvider(environments, hasMore),
            showZeroDataExperience: this._shouldShowZeroDataPage()
        }, () => {
            const resultCount = this.state.itemProvider.length;
            announce(resultCount > 0 ? format(Resources.FilteredDataCountText, resultCount) : Resources.NoResultsFound);
        });
    }

    private _getItemProvider(environments: IEnvironmentObject[], hasMore?: boolean): ArrayItemProvider<ITableItem> {
        let rows: ITableItem[] = environments.map<ITableItem>(env => {
            return {
                id: env.id,
                name: env.name,
                lastModifiedOn: env.lastModifiedOn,
                deploymentRecords: env.deploymentRecords,
                issue: env.issue
            } as ITableItem;
        });

        if (hasMore) {
            rows.push({
                isLoadRow: true,
                renderRow: this._renderLoadRow
            })
        }

        return new ArrayItemProvider<ITableItem>(rows);
    }

    private _getHeaderCommandBreakpoints = (): IHeaderBreakpoint[] => {
        return [
            {
                breakpoint: ScreenBreakpoints.xsmall,
                commandBarItems:[
                    {
                        id: "newEnvironment",
                        text: Resources.NewEnvironmentButtonShortText,
                        important: true,
                        iconProps: undefined,
                    }
                ]
            },
            {
                breakpoint: ScreenBreakpoints.small,
                commandBarItems:[
                    {
                        id: "newEnvironment",
                        text: Resources.NewEnvironmentButtonText,
                        important: true,
                        iconProps: undefined
                    }
                ]
            }
        ];
    }

    private _markTTI(): void {
        if (!this._isTTIMarked) {
            const scenarioName = this.state.showZeroDataExperience ? Constants.PerformanceScenarios.EnvironmentsZeroDataPageLoad : Constants.PerformanceScenarios.EnvironmentsListPageLoad;
            this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService").markTimeToInteractive("Environments", scenarioName);
            this._isTTIMarked = true;
        }
    }

    private _availableTabContent(): JSX.Element {
        return (
            <div className="env-page-content">
                {this.getErrorMessageView(Constants.EnvironmentsView)}
                {this._getFilterBar()}
                {this._getEnvironmentContentView()}
            </div>
        );
    }

    private _getEnvironmentContentView(): JSX.Element {
        if (this.state.showZeroDataExperience && this._filter.hasChangesToReset()) {
            if(this._filter.getState() && this._filter.getState().EnvironmentsNameFilter){
                announce(Resources.NoResultsFound, true);
            }    
            return this._getZeroDataContentForFilters();
        } else {
            const environmentsProvider = this.state.itemProvider;
            if(this._filter.getState() && this._filter.getState().EnvironmentsNameFilter){
                announce(format(Resources.FilteredDataCountText, environmentsProvider.length));
            }
            return (
                <Card className="bolt-card-no-vertical-padding" contentProps={{ contentPadding: false }}>
                    <div>
                        <Table
                            ariaLabel={Resources.EnvironmentsTitleName}
                            columns={this._sizableColumns}
                            tableBreakpoints={this._tableBreakpoints}
                            itemProvider={environmentsProvider}
                            onActivate={this._onRowClicked}
                        />
                    </div>
                </Card>
            );
        }
    }

    private _getAddEnvironmentDialogComponent(): JSX.Element | undefined {
        return (
            <Observer showPanel={this._addEnvironmentPanelOpened}>
                {(props: { showPanel: boolean }) => (
                    props.showPanel && (
                        <WrappedComponent
                            dependencies={["ms.vss-environments-web.environments-create-panel"]}
                            wrappedType={"addEnvironment"}
                            instanceId={this._instanceId}
                            onEnvironmentCreated={this._onNewEnvironmentCreated}
                            onDismissPanel={this._onDismissAddEnvironmentPanel}
                            loadingComponent={LoadingComponent}
                        />
                    )
                )}
            </Observer>
        );
    }

    private _shouldShowZeroDataPage(): boolean {
        return (
            this._store.getEnvironments().length === 0 &&
            !this._messagesStore.getMessage(Constants.EnvironmentsView)
        );
    }

    private _getZeroDataContent(): JSX.Element {
        const image = getAssetLocation(this.context.pageContext, "ms.vss-environments-web/environments-landing/ZeroData.svg");
        return (
            <div className="zero-data-container">
                <ZeroData
                    primaryText={Resources.ZeroDataPrimaryText}
                    imagePath={image}
                    imageAltText={Resources.ZeroDataImageAltText}
                    secondaryText={Resources.ZeroDataSecondaryText}
                    actionText={Resources.ZeroDataButtonText}
                    actionType={ZeroDataActionType.ctaButton}
                    onActionClick={this._onNewEnvironmentClicked}
                    className={"zero-data-content"}
                />
                {this._getAddEnvironmentDialogComponent()}
            </div>
        )
    }

    private _getZeroDataContentForFilters(): JSX.Element {
        const image = getAssetLocation(this.context.pageContext, "ms.vss-environments-web/environments-landing/no-results.png");
        return (
            <Card className="flex-grow bolt-card-no-vertical-padding environment-minimum-card-height" contentProps={{ contentPadding: false }}>
                <div className="zero-data-container">
                    <ZeroData
                        imageAltText={Resources.NoResultsFound}
                        imagePath={image}
                        secondaryText={Resources.NoResultsFound}
                        className={"zero-data-content"}
                    />
                </div>
            </Card>
        )
    }

    private _getFilterBar(): JSX.Element {
        return (
            <ConditionalChildren renderChildren={this._filterToggled}>
                <FilterBar filter={this._filter}>
                    <KeywordFilterBarItem filterItemKey={Constants.EnvironmentsNameFilterKey} placeholder={Resources.SearchEnvironmentsPlaceholder} throttleWait={400} />
                    <DropdownFilterBarItem
                        placeholder={Resources.EnvironmentStatusColumnName}
                        filterItemKey={Constants.DeploymentStatusFilterKey}
                        selection={this._stateSelection}
                        items={this._getPickListItems()}
                    />
                </FilterBar>
                <div className={"env-filter-spacer"} />
            </ConditionalChildren>
        )
    }

    private _getPickListItems(): string[] {
        return [Resources.DeploymentSucceeded, Resources.DeploymentFailed];
    }

    private _renderLoadRow = (index: number, item: ITableItem, details: ITableRowDetails<ITableItem>): JSX.Element => {
        this._actionCreator.fetchMoreEnvironments(
            this.context.pageContext,
            this._store.getState().continuationToken,
            this._getSearchTextValue(this._filter.getState()),
            this._getStatusFilterValue(this._filter.getState())
        );

        return (
            <TableRow index={index} details={details}>
                <SimpleTableCell contentClassName="envs-load-row no-v-padding justify-center" columnIndex={1} colspan={3}>
                    <Spinner label={Resources.Loading} />
                </SimpleTableCell>
            </TableRow>
        );
    }

    private _renderTabBarCommands = (): JSX.Element | undefined => {
        return this._selectedTabId.value === Constants.EnvironmentsDefaultTabKey ? <HeaderCommandBarWithFilter filter={this._filter} filterToggled={this._filterToggled} items={[]} /> : undefined;
    };

    private _getCommandBarItems(tabCount: number): IHeaderCommandBarItem[] {
        let items: IHeaderCommandBarItem[] = [];
        if (tabCount === 0) {
            items.push(getFilterItem(this._onFilterClicked, this._filter.hasChangesToReset()));
        }

        items = items.concat([
            {
                id: "newEnvironment",
                text: Resources.NewEnvironmentButtonText,
                ariaLabel: Resources.NewEnvironmentButtonText,
                isPrimary: true,
                onActivate: this._onNewEnvironmentClicked,
                important: true,
                rank: 1
            } as IHeaderCommandBarItem,
            {
                id: "environmentSecurity",
                text: Resources.Security,
                ariaLabel: Resources.Security,
                isPrimary: false,
                important: false,
                rank: 2,
                iconProps: {
                    iconName: "Lock"
                },
                onActivate: () => {
                    onClickFPS(this.context.pageContext, getEnvironmentsSecurityPageUrl(this.context.pageContext), true);
                }
            }
        ]);

        return items;
    }

    private _onNewEnvironmentClicked = (): void => {
        this._addEnvironmentPanelOpened.value = true;
    }

    private _onNewEnvironmentCreated = (environmentId: number): void => {
        announce(Resources.EnvironmentCreatedText, true);
        onClickFPS(this.context.pageContext, getEnvironmentPageUrl(this.context.pageContext, environmentId), true);
    }

    private _onDismissAddEnvironmentPanel = (environmentId?: number): void => {
        if (environmentId) {
            onClickFPS(this.context.pageContext, getEnvironmentPageUrl(this.context.pageContext, environmentId), true);
        } else {
            this._addEnvironmentPanelOpened.value = false;
        }
    }

    private _onSelectedTabChanged = (tabId: string): void => {
        this._selectedTabId.value = tabId;
    }

    private _onFilterApplied = (filterState: IFilterState): void => {
        let filtersApplied = this._filter.getState();
        this._actionCreator.getEnvironmentWithFilters(this.context.pageContext, true, this._getSearchTextValue(filtersApplied), this._getStatusFilterValue(filtersApplied));
    }

    private _onFilterClicked = () => {
        this._filterToggled.value = !this._filterToggled.value;
        this._filterToggled.value ? announce(Resources.FilterBarExpandedText, true) : announce(Resources.FilterBarCollapsedText, true);

    };

    private _getSearchTextValue = (filterState: IFilterState): string | undefined => {
        let searchFilterValue: string = "";
        let filterValue = filterState && filterState[Constants.EnvironmentsNameFilterKey];
        if (filterValue) {
            searchFilterValue = filterValue && filterValue.value;
        }
        return !!searchFilterValue ? "*" + searchFilterValue + "*" : undefined;
    }

    private _getStatusFilterValue = (filterState: IFilterState): EnvironmentJobStatus => {
        let statusFilterValue: EnvironmentJobStatus = EnvironmentJobStatus.None;
        let statusValue = filterState && filterState[Constants.DeploymentStatusFilterKey];
        let selectedStatusList = statusValue && statusValue.value;
        if (selectedStatusList) {
            selectedStatusList.forEach((status: string) => {
                if (status === Resources.DeploymentSucceeded) {
                    statusFilterValue |= EnvironmentJobStatus.Succeeded | EnvironmentJobStatus.SucceededWithIssues;
                } else if (status === Resources.DeploymentFailed) {
                    statusFilterValue |= EnvironmentJobStatus.Failed | EnvironmentJobStatus.Abandoned | EnvironmentJobStatus.Canceled | EnvironmentJobStatus.Skipped;
                } else {
                    statusFilterValue |= EnvironmentJobStatus.All;
                }
            });
        }

        return statusFilterValue;
    }

    private _onRowClicked = (event: any, row: ITableRow<ITableItem>): void => {
        if (row.data.id) {
            onClickFPS(this.context.pageContext, getEnvironmentPageUrl(this.context.pageContext, row.data.id), true);
        }
    }

    private _getNameElement(environmentName: string): React.ReactNode {
        return (
            <Tooltip overflowOnly={true} text={environmentName}>
                <span className={"font-weight-semibold body-m text-ellipsis"}>{environmentName}</span>
            </Tooltip>
        );
    }

    private _renderNameColumn = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ITableItem>, tableItem: ITableItem): JSX.Element => {
        return (
            <TableCell
                key={"environments-col-" + columnIndex}
                ariaLabel={Resources.EnvironmentColumnName + " " +tableItem.name}
                columnIndex={columnIndex}
                tableColumn={tableColumn}>
                <div className={css(this.props.contentClassName, "bolt-table-cell-content flex-row flex-center")}>
                    {tableItem && tableItem.name && this._getNameElement(tableItem.name)}
                    {tableItem.issue && (
                        <Icon
                            className={tableItem.issue.type === CheckIssueType.Error ? "environment-error-icon" : "environment-warning-icon"}
                            iconName={tableItem.issue.type === CheckIssueType.Error ? "ErrorBadge" : "Warning"}
                            tooltipProps={{
                                text: tableItem.issue.message
                            }}
                        />
                    )}
                </div>
            </TableCell>
        )
    }

    private _renderNameAndStatusColumn = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ITableItem>, tableItem: ITableItem): JSX.Element => {
        return (
          <TwoLineTableCell 
            className="no-v-padding"
            key={"nameAndStatusColumn-" + columnIndex}
            columnIndex={columnIndex}
            tableColumn={tableColumn}
            line1={
                tableItem &&
                tableItem.name &&
                this._getNameElement(tableItem.name)
            }
            line2={ 
                this._getStatusComponent(
                    rowIndex,
                    columnIndex,
                    tableColumn,
                    tableItem)
            }
          />
        );
    }

    private _renderLastUpdatedColumn = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ITableItem>, tableItem: ITableItem): JSX.Element => {
        return (
            <SimpleTableCell
                key={"lastupdated-col-" + columnIndex}
                ariaLabel={ Resources.EnvironmentUpdatedColumnName + " " + (tableItem.lastModifiedOn && tableItem.lastModifiedOn.toLocaleDateString())}
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                children={tableItem.lastModifiedOn && this._getlastUpdatedElement(tableItem.lastModifiedOn)}
            />
        )
    }

    private _getlastUpdatedElement(lastModifiedOn: Date): React.ReactNode {
        return (
            <Tooltip overflowOnly={true} text={lastModifiedOn.toLocaleDateString()}>
                <Ago format={AgoFormat.Compact} date={lastModifiedOn} />
            </Tooltip>
        );
    }

    private _renderStatusColumn = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ITableItem>, tableItem: ITableItem): JSX.Element => {
        return (
            <SimpleTableCell key={"status-" + columnIndex} columnIndex={columnIndex} tableColumn={tableColumn}>
                {this._getStatusComponent(rowIndex, columnIndex, tableColumn, tableItem)}
            </SimpleTableCell>
        )
    }

    private _getStatusComponent = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ITableItem>, tableItem: ITableItem): JSX.Element => {
        let lastCompletedRequestList: IEnvironmentDeploymentJobRecord[] | undefined = tableItem.deploymentRecords;
        if (tableItem.id) {
            if (!lastCompletedRequestList || lastCompletedRequestList.length === 0) {
                return (
                    <Tooltip overflowOnly={true} text={Resources.NeverDeployed}>
                        <span className="text-ellipsis">{Resources.NeverDeployed}</span>
                    </Tooltip>
                );
            }
            else {
                const statusProps = this._getStatus(lastCompletedRequestList);
                const statusText = this._getStatusIconToolTipMessage(statusProps);
                return (
                    <span className="flex-row scroll-hidden">
                        <Tooltip text={statusText}>
                            <span className="flex-row">
                                <Status {...statusProps} className="environment-icon-margin" size={StatusSize.m} ariaLabel={statusText} />
                            </span>
                        </Tooltip>
                        {this._getStatusMessageComponent(lastCompletedRequestList)}
                    </span>
                );
            }
        }
        else {
            return (
                <div />
            )
        }
    }

    private _getStatusMessageComponent(lastCompletedRequestList: IEnvironmentDeploymentJobRecord[]): JSX.Element {
        const deploymentMessage = format(Resources.lastDeploymentStatusMessage, lastCompletedRequestList[0].owner.name, lastCompletedRequestList[0].definition.name);
        return (
            <Tooltip overflowOnly={true} text={deploymentMessage}>
                <span className="body-m scroll-hidden">{deploymentMessage}</span>
            </Tooltip>
        );
    }

    private _getStatusIconToolTipMessage(statusProps: IStatusProps) {
        let toolTipMessage: string = Resources.DeploymentFailed;
        if (statusProps === Statuses.Success) {
            toolTipMessage = Resources.DeploymentSucceeded;
        }
        return toolTipMessage;
    }

    private _onSizeSizable = (event: MouseEvent, index: number, width: number, column: ITableColumn<ITableItem>): void => {
        (column.width as ObservableValue<number>).value = width;
    }

    private _tableBreakpoints: ITableBreakpoint[] = [
        {
            breakpoint: ScreenBreakpoints.xsmall,
            columnWidths: [0, 0, -240, 120]
        },
        {
            breakpoint: ScreenBreakpoints.small,
            columnWidths: [260, -100, 0, -120]
        },
        {
            breakpoint: ScreenBreakpoints.medium,
            columnWidths: [360, -240, 0, 150]
        },
        {
            breakpoint: ScreenBreakpoints.medium,
            columnWidths: [360, -240, 0, 150]
        }
    ];

    private _sizableColumns = [
        {
            id: "name",
            name: Resources.EnvironmentColumnName,
            width: new ObservableValue(362),
            className: "environments-cell",
            renderCell: this._renderNameColumn,
            onSize: this._onSizeSizable,
            ariaLabel: Resources.EnvironmentColumnName
        },
        {
            id: "status",
            name: Resources.EnvironmentStatusColumnName,
            width: new ObservableValue(-150),
            className: "environments-cell",
            renderCell: this._renderStatusColumn,
            onSize: this._onSizeSizable,
            ariaLabel: Resources.EnvironmentStatusColumnName
        },
        {
            id: "nameAndStatus",
            name: Resources.EnvironmentColumnName,
            width: new ObservableValue(-240),
            className: "environments-cell",
            renderCell: this._renderNameAndStatusColumn,
            onSize: this._onSizeSizable,
            ariaLabel: Resources.EnvironmentColumnName
        },
        {
            id: "lastUpdated",
            name: Resources.EnvironmentUpdatedColumnName,
            width: new ObservableValue(156),
            className: "environments-cell",
            renderCell: this._renderLastUpdatedColumn,
            onSize: this._onSizeSizable,
            ariaLabel: Resources.EnvironmentUpdatedColumnName
        }        
    ];

    private _getStatus(lastCompletedRequestList: IEnvironmentDeploymentJobRecord[]): IStatusProps {
        const lastCompletedRequestResult = lastCompletedRequestList[0].result;

        let statusProps: IStatusProps = Statuses.Running;

        if (lastCompletedRequestResult === TaskResult.Canceled ||
            lastCompletedRequestResult === TaskResult.Failed ||
            lastCompletedRequestResult === TaskResult.Abandoned ||
            lastCompletedRequestResult === TaskResult.Skipped) {
            statusProps = Statuses.Failed;
        }
        else if (lastCompletedRequestResult === TaskResult.Succeeded) {
            statusProps = Statuses.Success;
        }

        return statusProps;
    }

    private _stateSelection: ListSelection = new ListSelection({ alwaysMerge: true, multiSelect: true, selectOnFocus: false });
    private _store: EnvironmentsStore;
    private _actionCreator: EnvironmentsActionsCreator;
    private _isTTIMarked: boolean = false;
    private _tabProvider: IReadonlyObservableArray<IVssContributedTab>;
    private readonly _selectedTabId = new ObservableValue<string>(Constants.EnvironmentsDefaultTabKey);
    private readonly _filter = new Filter();
    private readonly _filterToggled = new ObservableValue<boolean>(false);
    private readonly _addEnvironmentPanelOpened = new ObservableValue<boolean>(false);
}

VssComponent.register("environmentsView", EnvironmentsComponent);
